import {testIdProps} from '@github-ui/test-id-props'
import {Link} from '@primer/react'
import {memo, useCallback, useRef} from 'react'

import {ItemType} from '../../../api/memex-items/item-type'
import {parseTitleDefaultHtml} from '../../../helpers/parsing'
import {useBoardSidePanel, useSidePanel} from '../../../hooks/use-side-panel'
import type {MemexItemModel} from '../../../models/memex-item-model'
import {SanitizedHtml} from '../../dom/sanitized-html'
import styles from './title.module.css'

type TitleProps = {
  item: MemexItemModel
  disableFocus: boolean
}

const TitleFunc: React.FC<TitleProps> = ({item, disableFocus}) => {
  const columnData = item.columns

  const html = parseTitleDefaultHtml(columnData.Title)

  switch (item.contentType) {
    case ItemType.RedactedItem:
      return <SanitizedHtml className={styles.SanitizedHtml}>{html}</SanitizedHtml>
    case ItemType.DraftIssue: {
      return <SidePanelLink item={item} disableFocus={disableFocus} />
    }
    case ItemType.Issue: {
      return <SidePanelLink item={item} disableFocus={disableFocus} />
    }
    case ItemType.PullRequest:
      return html ? (
        <Link
          href={item.content.url}
          target="_blank"
          rel="noreferrer"
          onClick={e => e.stopPropagation()}
          tabIndex={disableFocus ? -1 : 0}
          className={styles.Link}
          {...testIdProps('card-title-external-link')}
        >
          <h3 id={`board-card-title-${item.id}`} className="d-inline f5 text-normal">
            <SanitizedHtml>{html}</SanitizedHtml>
          </h3>
        </Link>
      ) : null
  }
}

export const Title = memo(TitleFunc)

Title.displayName = 'Title'

type SidePanelLinkProps = {
  item: MemexItemModel
  disableFocus: boolean
  ref?: React.Ref<HTMLElement>
}

const SidePanelLink = ({ref, item, disableFocus}: SidePanelLinkProps) => {
  const html = item.getHtmlTitle()
  const {openProjectItemInPane} = useSidePanel()
  const {openPane} = useBoardSidePanel()
  let href = '#'
  if (item.contentType === ItemType.Issue) {
    href = item.getUrl()
  }
  const linkRef = useRef<HTMLAnchorElement | null>(null)

  const openSidePanel = useCallback(
    (e: React.MouseEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      const isMetaOrCtrlClick = e.metaKey || e.ctrlKey
      const isDraftIssue = item.contentType === ItemType.DraftIssue

      // For non-draft issues with meta/ctrl key, allow default browser behavior (open in new tab)
      if (isMetaOrCtrlClick && !isDraftIssue) {
        // Prevent the click from bubbling up to the card selection handler
        e.stopPropagation()
        return
      }

      // For all other cases (normal clicks or draft issues with meta/ctrl), open side panel
      e.preventDefault()

      requestAnimationFrame(() => {
        const shouldFocusLink = linkRef?.current === document.activeElement
        const focusTarget = linkRef?.current

        if (shouldFocusLink && focusTarget) {
          openProjectItemInPane(item, () => {
            focusTarget.focus()
          })
        } else {
          openPane(item)
        }
      })
    },
    [item, openPane, openProjectItemInPane],
  )

  return (
    <Link
      ref={linkRef}
      role="button"
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={openSidePanel}
      tabIndex={disableFocus ? -1 : 0}
      {...testIdProps('card-side-panel-trigger')}
    >
      <h3 id={`board-card-title-${item.id}`} className="d-inline f5 text-normal">
        <SanitizedHtml ref={ref} className={styles.SanitizedHtml_1}>
          {html}
        </SanitizedHtml>
      </h3>
    </Link>
  )
}

SidePanelLink.displayName = 'SidePanelLink'
